export const ONE_HOUR_MS = 60 * 60 * 1000;

type TimestampedCacheEntry = {
  fetchedAt: number;
};

export function normalizeCacheValue(value: any): any {
  if (Array.isArray(value)) {
    // Preserve array order because some API params can be order-sensitive.
    return value.map((item) => normalizeCacheValue(item));
  }

  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc: Record<string, any>, key: string) => {
        acc[key] = normalizeCacheValue(value[key]);
        return acc;
      }, {});
  }

  return value;
}

export function cloneCacheValue<T>(value: T): T {
  if (value === undefined) {
    return value;
  }

  return JSON.parse(JSON.stringify(value));
}

export function buildUserScopedCacheKey(
  payload: any,
  userId: string,
  payloadKey: string
): string {
  const normalizedPayload = normalizeCacheValue(payload || {});

  return JSON.stringify({
    userId,
    [payloadKey]: normalizedPayload
  });
}

export function pruneExpiredCacheEntries<T extends TimestampedCacheEntry>(
  cache: Map<string, T>,
  ttlMs: number,
  now: number = Date.now()
): void {
  cache.forEach((entry, key) => {
    if (now - entry.fetchedAt >= ttlMs) {
      cache.delete(key);
    }
  });
}

export function setCacheEntryWithLimit<T>(
  cache: Map<string, T>,
  key: string,
  entry: T,
  maxEntries: number
): void {
  if (cache.has(key)) {
    // Refresh insertion order so this key becomes the newest entry.
    cache.delete(key);
  }

  cache.set(key, entry);

  while (cache.size > maxEntries) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) {
      break;
    }

    cache.delete(oldestKey);
  }
}
